import { useCallback } from "react";
import { Edge, Node, useReactFlow } from "@xyflow/react";
import { useSelector } from "react-redux";
import {
  selectSelectedElement,
  selectSelectedElementType,
} from "../../../app/slices/TrainingSlice";
import {
  ISequence,
  SequenceCommentZone,
} from "../../../models/interactions/Sequence";
import { applyDagreLayout } from "./applyDagreLayout";
import { SequenceNodeData } from "../SequenceNode";
import { useUpdateActiveSequence } from "../useUpdateActiveSequence.hook";
import {
  getVisibleSequenceEdges,
  getVisibleSequenceNodes,
} from "../takeSequenceEditor";

const DEFAULT_NODE_WIDTH = 256;
const DEFAULT_NODE_HEIGHT = 100;
const OVERLAP_GAP = 20;
const MAX_OVERLAP_ITERATIONS = 50;
const NODE_VISUAL_MARGIN_LEFT = 28;
const NODE_VISUAL_MARGIN_RIGHT = 28;
const NODE_VISUAL_MARGIN_TOP = 16;
const NODE_VISUAL_MARGIN_BOTTOM = 44;
const COMMENT_ZONE_LAYOUT_PADDING_X = 24;
const COMMENT_ZONE_LAYOUT_PADDING_TOP = 56;
const COMMENT_ZONE_LAYOUT_PADDING_BOTTOM = 32;

type NodeDimensions = Map<string, { width: number; height: number }>;

function clamp(value: number, min: number, max: number) {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

function getNodeDimensions(
  nodeId: string,
  nodeDimensions: NodeDimensions,
) {
  return nodeDimensions.get(nodeId) ?? {
    width: DEFAULT_NODE_WIDTH,
    height: DEFAULT_NODE_HEIGHT,
  };
}

function getNodeBounds(
  node: Node<SequenceNodeData>,
  nodeDimensions: NodeDimensions,
) {
  const { width, height } = getNodeDimensions(node.id, nodeDimensions);

  return {
    left: node.position.x - NODE_VISUAL_MARGIN_LEFT,
    top: node.position.y - NODE_VISUAL_MARGIN_TOP,
    right: node.position.x + width + NODE_VISUAL_MARGIN_RIGHT,
    bottom: node.position.y + height + NODE_VISUAL_MARGIN_BOTTOM,
  };
}

function isNodeFullyInsideZone(
  node: Node<SequenceNodeData>,
  zone: SequenceCommentZone,
  nodeDimensions: NodeDimensions,
) {
  const bounds = getNodeBounds(node, nodeDimensions);
  const zoneLeft = zone.position.x;
  const zoneTop = zone.position.y;
  const zoneRight = zone.position.x + zone.size.width;
  const zoneBottom = zone.position.y + zone.size.height;

  return (
    bounds.left >= zoneLeft &&
    bounds.top >= zoneTop &&
    bounds.right <= zoneRight &&
    bounds.bottom <= zoneBottom
  );
}

function getContainingCommentZone(
  node: Node<SequenceNodeData>,
  commentZones: SequenceCommentZone[],
  nodeDimensions: NodeDimensions,
) {
  return commentZones.find((zone) =>
    isNodeFullyInsideZone(node, zone, nodeDimensions),
  );
}

function getLayoutBounds(
  nodes: Node<SequenceNodeData>[],
  nodeDimensions: NodeDimensions,
) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  nodes.forEach((node) => {
    const { width, height } = getNodeDimensions(node.id, nodeDimensions);
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + width);
    maxY = Math.max(maxY, node.position.y + height);
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function translateNodes(
  nodes: Node<SequenceNodeData>[],
  tx: number,
  ty: number,
) {
  return nodes.map((node) => ({
    ...node,
    position: {
      x: node.position.x + tx,
      y: node.position.y + ty,
    },
  }));
}

function translateLayoutToZone(
  layoutedNodes: Node<SequenceNodeData>[],
  originalNodes: Node<SequenceNodeData>[],
  zone: SequenceCommentZone,
  nodeDimensions: NodeDimensions,
) {
  if (layoutedNodes.length === 0) {
    return layoutedNodes;
  }

  const layoutBounds = getLayoutBounds(layoutedNodes, nodeDimensions);
  const originalBounds = getLayoutBounds(originalNodes, nodeDimensions);
  const contentLeft = zone.position.x + COMMENT_ZONE_LAYOUT_PADDING_X;
  const contentTop = zone.position.y + COMMENT_ZONE_LAYOUT_PADDING_TOP;
  const contentRight =
    zone.position.x + zone.size.width - COMMENT_ZONE_LAYOUT_PADDING_X;
  const contentBottom =
    zone.position.y + zone.size.height - COMMENT_ZONE_LAYOUT_PADDING_BOTTOM;
  const maxMinX = Math.max(contentLeft, contentRight - layoutBounds.width);
  const maxMinY = Math.max(contentTop, contentBottom - layoutBounds.height);
  const anchoredMinX = clamp(originalBounds.minX, contentLeft, maxMinX);
  const anchoredMinY = clamp(originalBounds.minY, contentTop, maxMinY);

  return translateNodes(
    layoutedNodes,
    anchoredMinX - layoutBounds.minX,
    anchoredMinY - layoutBounds.minY,
  );
}

function translateLayoutToAvoidOverlap(
  layoutedNodes: Node<SequenceNodeData>[],
  originalNodes: Node<SequenceNodeData>[],
  nonFormattedNodes: Node<SequenceNodeData>[],
  nodeDimensions: NodeDimensions,
) {
  if (layoutedNodes.length === 0) {
    return layoutedNodes;
  }

  const originalBounds = getLayoutBounds(originalNodes, nodeDimensions);
  const layoutBounds = getLayoutBounds(layoutedNodes, nodeDimensions);
  let tx = originalBounds.minX - layoutBounds.minX;
  let ty = originalBounds.minY - layoutBounds.minY;

  for (let iteration = 0; iteration < MAX_OVERLAP_ITERATIONS; iteration++) {
    let needDx = 0;
    let needDy = 0;

    for (const node of layoutedNodes) {
      const { width, height } = getNodeDimensions(node.id, nodeDimensions);
      const left = node.position.x + tx;
      const top = node.position.y + ty;
      const right = left + width;
      const bottom = top + height;

      for (const other of nonFormattedNodes) {
        const otherDimensions = getNodeDimensions(other.id, nodeDimensions);
        const otherLeft = other.position.x;
        const otherTop = other.position.y;
        const otherRight = otherLeft + otherDimensions.width;
        const otherBottom = otherTop + otherDimensions.height;
        const overlapX = Math.min(right, otherRight) - Math.max(left, otherLeft);
        const overlapY =
          Math.min(bottom, otherBottom) - Math.max(top, otherTop);

        if (overlapX > 0 && overlapY > 0) {
          needDx = Math.max(needDx, overlapX + OVERLAP_GAP);
          needDy = Math.max(needDy, overlapY + OVERLAP_GAP);
        }
      }
    }

    if (needDx === 0 && needDy === 0) {
      break;
    }

    tx += needDx;
    ty += needDy;
  }

  return translateNodes(layoutedNodes, tx, ty);
}

function measureNodeDimensions(
  nodesToMeasure: Node<SequenceNodeData>[],
  reactFlowNodes: Node[],
  screenToFlowPosition: (position: { x: number; y: number }) => {
    x: number;
    y: number;
  },
) {
  const nodeDimensions: NodeDimensions = new Map();

  nodesToMeasure.forEach((node) => {
    let width = DEFAULT_NODE_WIDTH;
    let height = DEFAULT_NODE_HEIGHT;
    const reactFlowNode = reactFlowNodes.find((candidate) => candidate.id === node.id);

    if (reactFlowNode?.width && reactFlowNode?.height) {
      width = reactFlowNode.width;
      height = reactFlowNode.height;
    }

    if (width === DEFAULT_NODE_WIDTH && height === DEFAULT_NODE_HEIGHT) {
      const selectors = [
        `.react-flow__node[data-id="${node.id}"]`,
        `[data-id="${node.id}"]`,
        `#${node.id}`,
        `.react-flow__node[id="${node.id}"]`,
      ];

      let nodeElement: HTMLElement | null = null;
      for (const selector of selectors) {
        nodeElement = document.querySelector(selector) as HTMLElement;
        if (nodeElement) {
          break;
        }
      }

      if (!nodeElement) {
        const reactFlowContainer = document.querySelector(".react-flow");
        if (reactFlowContainer) {
          for (const selector of selectors) {
            nodeElement = reactFlowContainer.querySelector(selector) as HTMLElement;
            if (nodeElement) {
              break;
            }
          }
        }
      }

      if (nodeElement) {
        const rect = nodeElement.getBoundingClientRect();
        const topLeft = screenToFlowPosition({
          x: rect.left,
          y: rect.top,
        });
        const bottomRight = screenToFlowPosition({
          x: rect.right,
          y: rect.bottom,
        });
        width = Math.abs(bottomRight.x - topLeft.x) || DEFAULT_NODE_WIDTH;
        height = Math.abs(bottomRight.y - topLeft.y) || DEFAULT_NODE_HEIGHT;
      }
    }

    nodeDimensions.set(node.id, { width, height });
  });

  return nodeDimensions;
}

export function useSequenceLayout() {
  const interaction = useSelector(selectSelectedElement) as ISequence;
  const selectedElementType = useSelector(selectSelectedElementType);
  const { fitView, getNodes, screenToFlowPosition } = useReactFlow();
  const { updateSequence } = useUpdateActiveSequence();

  const applyLayout = useCallback(() => {
    if (!interaction.nodes || interaction.nodes.length === 0) {
      return;
    }

    const realNodes = interaction.nodes.filter(
      (node) => node.id !== "temp-target",
    ) as Node<SequenceNodeData>[];
    const realEdges = (interaction.edges || []).filter(
      (edge) => !edge.id?.startsWith("temp-connection-"),
    ) as Edge[];
    const visibleNodes = getVisibleSequenceNodes(realNodes, selectedElementType);
    const visibleEdges = getVisibleSequenceEdges(
      realNodes,
      realEdges,
      selectedElementType,
    );
    const commentZones = interaction.commentZones ?? [];

    setTimeout(() => {
      const reactFlowNodes = getNodes();
      const selectedNodes = reactFlowNodes.filter((node) => node.selected);
      const shouldFormatSelection = selectedNodes.length > 1;
      const selectedNodeIds = new Set(selectedNodes.map((node) => node.id));
      const nodesToFormat = shouldFormatSelection
        ? visibleNodes.filter((node) => selectedNodeIds.has(node.id))
        : visibleNodes;
      const edgesToFormat = shouldFormatSelection
        ? visibleEdges.filter(
            (edge) =>
              selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target),
          )
        : visibleEdges;

      if (nodesToFormat.length === 0) {
        return;
      }

      const nodesToMeasure = shouldFormatSelection ? visibleNodes : nodesToFormat;
      const nodeDimensions = measureNodeDimensions(
        nodesToMeasure,
        reactFlowNodes,
        screenToFlowPosition,
      );

      if (shouldFormatSelection) {
        visibleNodes.forEach((node) => {
          if (!nodeDimensions.has(node.id)) {
            const reactFlowNode = reactFlowNodes.find((candidate) => candidate.id === node.id);
            nodeDimensions.set(node.id, {
              width: reactFlowNode?.width ?? DEFAULT_NODE_WIDTH,
              height: reactFlowNode?.height ?? DEFAULT_NODE_HEIGHT,
            });
          }
        });
      }

      const groups = new Map<
        string,
        {
          zone: SequenceCommentZone | null;
          nodes: Node<SequenceNodeData>[];
        }
      >();

      nodesToFormat.forEach((node) => {
        const zone = getContainingCommentZone(node, commentZones, nodeDimensions);
        const groupKey = zone?.id ?? "__outside__";
        const group = groups.get(groupKey) ?? { zone: zone ?? null, nodes: [] };
        group.nodes.push(node);
        groups.set(groupKey, group);
      });

      const layoutedNodes = Array.from(groups.values()).flatMap((group) => {
        const groupNodeIds = new Set(group.nodes.map((node) => node.id));
        const groupEdges = edgesToFormat.filter(
          (edge) => groupNodeIds.has(edge.source) && groupNodeIds.has(edge.target),
        );
        const dagreLayout = applyDagreLayout(
          group.nodes,
          groupEdges,
          nodeDimensions,
        );

        if (group.zone) {
          return translateLayoutToZone(
            dagreLayout,
            group.nodes,
            group.zone,
            nodeDimensions,
          );
        }

        if (shouldFormatSelection) {
          const layoutedNodeIds = new Set(dagreLayout.map((node) => node.id));
          const nonFormattedNodes = visibleNodes.filter(
            (node) => !layoutedNodeIds.has(node.id),
          );

          return translateLayoutToAvoidOverlap(
            dagreLayout,
            group.nodes,
            nonFormattedNodes,
            nodeDimensions,
          );
        }

        return dagreLayout;
      });

      const layoutedNodeIds = new Set(layoutedNodes.map((node) => node.id));
      const updatedNodes = realNodes.map((node) => {
        if (!layoutedNodeIds.has(node.id)) {
          return node;
        }

        return layoutedNodes.find((layoutedNode) => layoutedNode.id === node.id) ?? node;
      });

      updateSequence({
        ...interaction,
        nodes: updatedNodes,
      } as ISequence);

      setTimeout(() => {
        fitView({ padding: 0.2, duration: 300 });
      }, 50);
    }, 100);
  }, [
    fitView,
    getNodes,
    interaction,
    screenToFlowPosition,
    selectedElementType,
    updateSequence,
  ]);

  return { applyLayout };
}
